import useIsomorphicLayoutEffect from '@shared/hooks/useIsomorphicLayoutEffect'
import { useScroll } from 'framer-motion'
import { startTransition, useEffect, useState } from 'react'

const offsetVar = '--mobile-search-bottom-offset'
/** Only relevant for mobile search bar */
export function useSearchBottomOffset(offset: string): void {
  useEffect(() => {
    window.requestAnimationFrame(() => {
      document.querySelector('html')?.style.setProperty(offsetVar, offset)
    })
    return () => {
      document.querySelector('html')?.style.removeProperty(offsetVar)
    }
  }, [offset])
}

/**
 * Scroll offset at which the home page NavBar leaves its ghost state and morphs back
 * into the full bar. Must stay below the hero's first element (~97px) so the fixed bar
 * never overlaps it, and above trackpad jitter so the bar doesn't flip back and forth.
 */
export const GHOST_THRESHOLD = 80

export function useIsTop(yThreshold: number = 300) {
  // The server has no scroll position, so the initial value must be the same on both sides —
  // reading `window.scrollY` here would hydrate a scrolled reload against mismatching markup.
  // The layout effect below applies the real position before the first paint instead.
  const [isTop, setIsTop] = useState(true)
  const { scrollY } = useScroll()
  useIsomorphicLayoutEffect(() => {
    function checkIfAtTop(y: number) {
      if (y < yThreshold) {
        // On mobile a scroll event is triggered when a body scroll lock is active
        // So when an overlay is open the navbar would go transparent even though the page is not scrolled to the top
        // To fix this we skip the top check while a lock is active
        if (document.body.hasAttribute('data-dialog-prevent-body-scroll')) {
          return
        }
        setIsTop(true)
      } else {
        setIsTop(false)
      }
    }
    checkIfAtTop(scrollY.get())
    return scrollY.on('change', (y) => {
      startTransition(() => {
        checkIfAtTop(y)
      })
    })
  }, [scrollY, yThreshold])

  return isTop
}
